import {useMemo, useState} from 'react';
import {Box} from 'src/design/atoms/Box';
import {Stack} from 'src/design/atoms/Stack';
import {IconGauge} from '@tabler/icons-react';
import {
  FeatureFlagEnum,
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {AssistantAnswerQualitySection} from 'src/app/assistant/ControlCenter/AssistantAnswerQualitySection';
import {AssistantConversationsTable} from 'src/app/assistant/ControlCenter/AssistantConversationsTable';
import {AssistantFeedbackTable} from 'src/app/assistant/ControlCenter/AssistantFeedbackTable';
import {AssistantToolUseSection} from 'src/app/assistant/ControlCenter/AssistantToolUseSection';
import {AssistantTranscriptDrawer} from 'src/app/assistant/ControlCenter/AssistantTranscriptDrawer';
import {AssistantUsageChart} from 'src/app/assistant/ControlCenter/AssistantUsageChart';
import {AssistantUsageSection} from 'src/app/assistant/ControlCenter/AssistantUsageSection';
import {
  formatPercent,
  formatTokens,
} from 'src/app/assistant/ControlCenter/assistantUsageFormat';
import {SERIES_DAYS} from 'src/app/assistant/ControlCenter/controlCenterParams';
import type {
  ControlCenterView,
  OpenTranscript,
} from 'src/app/assistant/ControlCenter/controlCenterParams';
import {useControlCenterView} from 'src/app/assistant/ControlCenter/useControlCenterUrlState';
import {AssistantUserMemoryDrawer} from 'src/app/assistant/Memory/AssistantUserMemoryDrawer';
import {AssistantCustomOperations} from 'src/shared/auth/permissions/customOperations';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {
  useAssistantOverviewQuery,
  useAssistantToolStatsQuery,
  useAssistantTranscriptQuery,
  useAssistantUsageSeriesQuery,
  useAssistantUserUsageQuery,
} from 'src/redux/queries/assistantControlCenter';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {useUrlQueryTabV2} from 'src/shared/hooks/useUrlQueryTabV2';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import type {ISelectOption} from 'src/types/Form';
import {AssistantUserUsageTable} from './components/AssistantUserUsageTable';

export const AssistantControlCenterPage = () => {
  useSetDocumentTitle('Scout Usage');

  const {view, selectView} = useControlCenterView();
  const [openTranscript, setOpenTranscript] = useState<OpenTranscript | null>(
    null
  );
  const [memoryUser, setMemoryUser] = useState<{
    userId: number;
    userName: string;
  } | null>(null);

  const {
    data: overview,
    isLoading: overviewLoading,
    isError: overviewError,
  } = useAssistantOverviewQuery();
  const {
    data: series,
    isLoading: seriesLoading,
    isError: seriesError,
  } = useAssistantUsageSeriesQuery(SERIES_DAYS);
  const {
    data: users,
    isLoading: usersLoading,
    isError: usersError,
  } = useAssistantUserUsageQuery(SERIES_DAYS);
  const {
    data: toolStats,
    isLoading: toolStatsLoading,
    isError: toolStatsError,
  } = useAssistantToolStatsQuery(SERIES_DAYS);

  const {
    data: transcript,
    isFetching: transcriptFetching,
    isError: transcriptError,
  } = useAssistantTranscriptQuery(openTranscript?.conversationId ?? 0, {
    skip: openTranscript === null,
  });

  const userOptions = useMemo<ISelectOption[]>(
    () =>
      (users ?? []).map(u => ({
        id: u.userId,
        label: u.userName ?? `User ${u.userId}`,
      })),
    [users]
  );

  const usageStats = overview
    ? [
        {label: 'Tokens today', value: formatTokens(overview.today.totalTokens)},
        {
          label: 'Budget used today',
          value: formatPercent(overview.todayInstanceBudgetPercent),
        },
        {label: 'Active users today', value: overview.todayActiveUsers},
        {
          label: 'Conversations (30d)',
          value: overview.last30ConversationCount,
        },
        {label: 'Messages (30d)', value: overview.last30MessageCount},
      ]
    : [];

  const {RenderedTabsComponent} = useUrlQueryTabV2<ControlCenterView>({
    view,
    defaultView: 'overview',
    onSelectView: selectView,
    tabs: [
      {
        view: 'overview',
        label: 'Overview',
        component: (
          <Stack gap="lg">
            <AssistantUsageSection
              stats={usageStats}
              isLoading={overviewLoading}
              isError={overviewError}>
              <AssistantUsageChart
                series={series}
                isLoading={seriesLoading}
                isError={seriesError}
                showCost={false}
              />
            </AssistantUsageSection>
            <AssistantAnswerQualitySection
              tally={overview?.feedback}
              isLoading={overviewLoading}
              isError={overviewError}
              scopeText="the last 30 days"
            />
            <AssistantToolUseSection
              stats={toolStats}
              isLoading={toolStatsLoading}
              isError={toolStatsError}
              scopeText="the last 30 days"
            />
            <SectionCard title="Users">
              <AssistantUserUsageTable
                data={users ?? []}
                isLoading={usersLoading}
                isError={usersError}
                onViewMemory={user =>
                  setMemoryUser({
                    userId: user.userId,
                    userName: user.userName ?? `User ${user.userId}`,
                  })
                }
              />
            </SectionCard>
          </Stack>
        ),
      },
      {
        view: 'conversations',
        label: 'Conversations',
        component: (
          <SectionCard title="Conversations">
            <AssistantConversationsTable
              scope="instance"
              userOptions={userOptions}
              showCost={false}
              onViewTranscript={id => setOpenTranscript({conversationId: id})}
            />
          </SectionCard>
        ),
      },
      {
        view: 'feedback',
        label: 'Feedback',
        component: (
          <SectionCard title="Feedback">
            <AssistantFeedbackTable
              scope="instance"
              userOptions={userOptions}
              onViewConversation={(conversationId, focusMessageId) =>
                setOpenTranscript({conversationId, focusMessageId})
              }
            />
          </SectionCard>
        ),
      },
    ],
  });

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Assistant}>
      <Stack gap="lg">
        <PageHeader>
          <PageHeader.Title
            icon={<IconGauge size={24} />}
            subtitle="Token usage, reply feedback, and conversation activity for Scout">
            Scout Usage
          </PageHeader.Title>
        </PageHeader>
        <Box>{RenderedTabsComponent}</Box>
      </Stack>

      <AssistantTranscriptDrawer
        opened={openTranscript !== null}
        onClose={() => setOpenTranscript(null)}
        transcript={transcript}
        isLoading={transcriptFetching}
        isError={transcriptError}
        showCost={false}
        focusMessageId={openTranscript?.focusMessageId}
      />
      <AssistantUserMemoryDrawer
        user={memoryUser}
        onClose={() => setMemoryUser(null)}
      />
    </ContentWrapper>
  );
};

routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.AssistantControlCenter(),
  element: <AssistantControlCenterPage />,
  module: ModuleEnum.Assistant,
  operation: PermissionOperationEnum.Custom,
  operationName: AssistantCustomOperations.controlCenter,
  isPrivate: true,
});
